import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { InviteEntry, InviteEntryData } from "./InviteEntry";
import { useToast } from "@/hooks/use-toast";
import { useInviteFormState } from "./use-invite-form-state";
import { InviteConfirmationDialog } from "./InviteConfirmationDialog";
import type { SubmissionResult } from "./use-invite-submission";
import { validateAllEntries } from "./InviteValidation";
import { useProductEvents } from "@/hooks/use-product-events";

export interface InviteFormSubmissionResult {
  successful: InviteEntryData[];
  failed: Array<{ entry: InviteEntryData; error: string }>;
  apiResults: {
    successful: Array<{ entry: InviteEntryData; response: any }>;
    failed: Array<{ entry: InviteEntryData; error: string }>;
  };
}

interface InviteFormProps {
  onSave: (result: InviteFormSubmissionResult) => void | Promise<void>;
  maxEntries?: number;
  entityLabel?: string; // e.g., "Contractor" or "Team Member"
  idLabel?: string; // e.g., "Contractor ID" or "Team Member ID"
  showCustomerHistory?: boolean; // Whether to show the customer history file field
  showIdField?: boolean; // Whether to show the ID field (default: true)
  onSkip?: () => void; // Optional skip handler
}

const MAX_ENTRIES = 20;

export function InviteForm({
  onSave,
  maxEntries = MAX_ENTRIES,
  entityLabel = i18n.t("components.invites.inviteForm.contractor"),
  idLabel = i18n.t("components.invites.inviteForm.customerId"),
  showCustomerHistory = true,
  showIdField = true,
  onSkip,
}: InviteFormProps) {
  const { t } = useTranslation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validEntriesToSubmit, setValidEntriesToSubmit] = useState<
    InviteEntryData[]
  >([]);
  const { toast } = useToast();
  const { capture } = useProductEvents({ component: "InviteForm" });

  const validationOptions = { requireId: showIdField };
  const formState = useInviteFormState(maxEntries, validationOptions);
  const {
    entries,
    entryErrors,
    addEntry,
    removeEntry,
    updateEntry,
    hasValidEntries,
    clearErrors,
    setErrors,
  } = formState;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    const { successful, failed, errors } = validateAllEntries(entries, {
      requireId: showIdField,
    });
    setErrors(errors);
    if (failed.length > 0) {
      toast({
        title: t("components.invites.inviteForm.pleaseFixErrors"),
        description: t("components.invites.inviteForm.pleaseFixAllErrors"),
        variant: "destructive",
      });
      return;
    }

    // Capture pre_send_invites event
    capture({
      event: "pre_send_invites",
      context: {
        inviteCount: successful.length,
        totalEntries: entries.length,
        hasValidEntries: hasValidEntries,
      },
    });

    // Only open dialog if all entries are valid
    setValidEntriesToSubmit(successful);
    setShowConfirmDialog(true);
  };

  const handleSubmissionComplete = async (result: SubmissionResult) => {
    const { successful, failed } = validateAllEntries(entries, {
      requireId: showIdField,
    });
    await onSave({
      successful,
      failed,
      apiResults: result,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          {entries.map((entry, index) => (
            <InviteEntry
              key={index}
              index={index}
              value={entry}
              onChange={(data) => updateEntry(index, data)}
              onRemove={() => removeEntry(index)}
              canRemove={entries.length > 1}
              error={entryErrors[index] || null}
              entityLabel={entityLabel}
              idLabel={idLabel}
              showCustomerHistory={showCustomerHistory}
              showIdField={showIdField}
            />
          ))}
        </div>

        {entries.length < maxEntries && (
          <Button
            type="button"
            variant="outline"
            onClick={addEntry}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("components.invites.inviteForm.addAnother")} {entityLabel} ({entries.length}/{maxEntries})
          </Button>
        )}

        <div className="flex justify-between items-center gap-4 pt-4 border-t">
          {onSkip && (
            <Button
              type="button"
              variant="ghost"
              onClick={onSkip}
              className="text-muted-foreground"
            >
              {t("components.invites.inviteForm.skipForNow")}
            </Button>
          )}
          <div className={onSkip ? "ml-auto" : ""}>
            <Button
              type="submit"
              disabled={!hasValidEntries}
              className="min-w-32"
            >
              {t("components.invites.inviteForm.sendInvites")}
            </Button>
          </div>
        </div>
      </form>

      {/* Confirmation Dialog */}
      <InviteConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        validEntries={validEntriesToSubmit}
        allEntries={entries}
        onComplete={handleSubmissionComplete}
        entityLabel={entityLabel}
        idLabel={idLabel}
        showIdField={showIdField}
      />
    </>
  );
}
